import React, { useId } from "react"
import { Input as InputPrimitive } from "@base-ui/react/input"
import type { InputChangeEventDetails } from "@base-ui/react/input"
import { VisuallyHidden } from "../visually-hidden"

export type { InputChangeEventDetails }

// =============================================================================
// Styles
// =============================================================================
// The 1px edge is an inset ring rather than a border so the 24px line plus
// 16px of padding lands on the 40px target height, the way `Button` does.
export const inputSurfaceStyles = [
  "rounded inset-ring inset-ring-gray-300 bg-white transition", // surface
  "hover:inset-ring-gray-400", // hover
  // One focus line serves both hosts: an input never has children, and a box never takes focus itself
  "outline-2 outline-offset-2 outline-transparent focus-visible:outline-gray-900 has-[[data-slot=input]:focus-visible]:outline-gray-900"
]

export const inputTypographyStyles =
  "font-sans text-sm leading-6 text-gray-900 placeholder:text-gray-600"

// =============================================================================
// Props
// =============================================================================
/** The text-like input types. Checkbox, radio, file and hidden are other components or a plain element. */
export type InputType =
  | "text"
  | "email"
  | "number"
  | "password"
  | "search"
  | "tel"
  | "url"

export type InputBaseProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  | "type"
  | "size"
  | "className"
  | "style"
  | "children"
  | "aria-invalid"
  | "aria-label"
> & {
  /** Which kind of text the input takes */
  type?: InputType
  /** Marks the value as invalid: red ring and `aria-invalid`. Use this rather than `aria-invalid` so there is one source for the state. */
  invalid?: boolean
  /**
   * Names the input for screen readers when it has no visible label, as in a
   * search box that is only a magnifier icon. Rendered as visually hidden text
   * rather than `aria-label`, so it goes through translation like any other
   * copy. Prefer a `Field.Label` whenever there is room for one.
   */
  accessibleName?: string
  /** Called with the new value as a string, alongside the native `onChange` */
  onValueChange?: (value: string, eventDetails: InputChangeEventDetails) => void
  /** CSS classes. `InputBase` has no styles of its own; the host supplies them. */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * The input behaviour with none of the looks: the prop API, the accessible
 * name, the invalid state and the password-manager attributes, and no visual
 * styling at all.
 *
 * A building block rather than a control to reach for on a page. Its two hosts
 * are `Input`, which draws the standalone control, and `InputGroup.Input`,
 * which drops the chrome the group draws instead. Use one of those.
 */
export const InputBase = React.forwardRef<HTMLInputElement, InputBaseProps>(
  function InputBase(
    {
      type = "text",
      invalid,
      accessibleName,
      autoComplete,
      className,
      ...props
    },
    ref
  ) {
    const accessibleNameId = useId()

    return (
      <>
        <InputPrimitive
          {...props}
          {...(invalid ? { "aria-invalid": true } : {})}
          {...(accessibleName ? { "aria-labelledby": accessibleNameId } : {})}
          // For password managers, which ignore `autocomplete="off"`
          {...(autoComplete === "off"
            ? { "data-lpignore": "true", "data-1p-ignore": "true" }
            : {})}
          ref={ref}
          type={type}
          autoComplete={autoComplete}
          data-slot="input"
          className={className}
        />
        {/* A sibling, not a wrapper, so the input keeps its place in the
            parent's flex or grid layout. `sr-only` positions it absolutely. */}
        {accessibleName && (
          <VisuallyHidden id={accessibleNameId}>
            {accessibleName}
          </VisuallyHidden>
        )}
      </>
    )
  }
)

InputBase.displayName = "InputBase"
